import {useCallback, useMemo, useState} from "react";
import {useRouter} from "next/navigation";
import {useCompanyId} from "@/hooks/use-company-id";
import {useBoundStore} from "@/hooks/queries/stores/use-bound-store";
import {useTemplates} from "@/hooks/queries/use-templates";
import {DocumentEntity, DocumentType, ProjectEntity} from "@/pb/api/devplan/types/documents";
import {documentPath} from "@/app/paths";
import {CreateEmptyDocumentRequest} from "@/pb/api/devplan/services/web/company";
import {toast} from "sonner";
import {DocumentTemplate} from "@/pb/api/devplan/types/templates";

/**
 * Find the discovery document for a given idea project
 */
export function findDiscoveryDoc(documents: Record<string, DocumentEntity>, projectId: string) {
  return Object.values(documents).find(
    doc => doc.projectId === projectId && doc.type === DocumentType.IDEA_DISCOVERY
  )
}

/**
 * Create a discovery document for an idea project
 * Returns the document ID
 */
export async function createDiscoveryDocument(
  companyId: number,
  project: ProjectEntity,
  templates: { documentTemplates?: DocumentTemplate[] } | undefined,
  createEmptyDocument: (companyId: number, request: CreateEmptyDocumentRequest) => Promise<string>
): Promise<string> {
  const placeholderTemplate = templates?.documentTemplates?.find(
    t => t.type?.$case === "customDocument"
  )

  if (!placeholderTemplate) {
    throw new Error("No placeholder template found. Please contact support.")
  }

  const docId = await createEmptyDocument(companyId, CreateEmptyDocumentRequest.create({
    projectId: project.id,
    templateId: placeholderTemplate.id,
    title: "Discovery",
    type: DocumentType.IDEA_DISCOVERY,
    unique: true,
  }))

  return docId
}

/**
 * Hook to manage idea discovery document creation and navigation
 * Handles both existing discovery docs and creating new ones
 */
export function useIdeaDiscovery(projectId: string) {
  const {companyId} = useCompanyId()
  const router = useRouter()
  const project = useBoundStore(s => s.projects[projectId])
  const {createEmptyDocument, documents} = useBoundStore()
  const {templates} = useTemplates()
  const [isCreatingDiscovery, setIsCreatingDiscovery] = useState(false)

  // Check if discovery document already exists for this project
  const existingDiscoveryDoc = useMemo(() => {
    return findDiscoveryDoc(documents, projectId)
  }, [documents, projectId])

  const handleStartDiscovery = useCallback(async () => {
    if (!project) return

    // If discovery already exists, navigate to it
    if (existingDiscoveryDoc) {
      router.push(documentPath(companyId, existingDiscoveryDoc.id))
      return
    }

    // Create new discovery document
    setIsCreatingDiscovery(true)
    try {
      const docId = await createDiscoveryDocument(companyId, project, templates, createEmptyDocument)
      router.push(documentPath(companyId, docId))
    } catch (e) {
      toast.error(`Failed to create discovery: ${e}`)
    } finally {
      setIsCreatingDiscovery(false)
    }
  }, [companyId, createEmptyDocument, existingDiscoveryDoc, project, templates, router])

  return {
    handleStartDiscovery,
    isCreatingDiscovery,
    existingDiscoveryDoc
  }
}
